import React, { useEffect, useState, useTransition } from "react";
import Link from "next/link";
import { PostItemProps } from "@/types/modules/post/components";
import { AuthorType } from "@/core/types/contentTypes";
import { TinyBookmarkButton } from "@/components/ui-kit/Buttons/TinyBookmarkButton";
import { DottedSeparator } from "@/components/ui-kit/Decorations/DottedSeparator";
import { ImageWithSkeleton } from "@/components/ui-kit/ImageWithSkeleton";
import { TinyDownloadButton } from "@/components/ui-kit/Buttons/TinyDownloadButton";
import styles from "@/styles/post.module.css";
import { TinyPlayAudioButton } from "@/components/ui-kit/Buttons/Audio/TinyPlayAudioButton";
import { TinyPauseAudioButton } from "@/components/ui-kit/Buttons/Audio/TinyPauseAudioButton";
import { useAudioPlayer } from "@/hooks/useAudioPlayer";
import { useOfflinePostContent } from "@/hooks/indexeddb/useOfflinePostContent";
import { pushToDataLayer } from "@/utils/pushToDataLayer";

export const RelatedPostItem: React.FC<PostItemProps> = ({ post, isRelatedInArticle = false, onClick }) => {
  const [hoveredPost, setHoveredPost] = useState(false);
  const [offlineThumbnailBlob, setOfflineThumbnailBlob] = useState<string>();
  const [isTrackDownloaded, setIsTrackDownloaded] = useState<boolean>(false);
  const [shouldBeGone, setShouldBeGone] = useState<boolean>(false);
  const [isPending, startTransition] = useTransition();
  const { setTrack, play, pause, isPaused, currentTrack } = useAudioPlayer();
  const { saveOrDelete, downloading, isDownloaded, offlinePostThumbnail } = useOfflinePostContent();
  const isPlaying = currentTrack?.id === post.id;

  useEffect(() => {
    if (post?.id) {
      isDownloaded(post.id).then((res) => {
        setIsTrackDownloaded(res);
      });
      offlinePostThumbnail(post).then((res) => {
        setOfflineThumbnailBlob(res);
      });
    }
  }, [post, isDownloaded, offlinePostThumbnail]);

  const handleRelatedPostLink = () => {
    if (onClick) {
      onClick();
    }

    // article_interaction (related_article)
    pushToDataLayer({
      event: "article_interaction",
      interaction_type: `${isRelatedInArticle ? "related_in-article" : "related-article"}`,
      interaction_option: post.title ?? "article",
    });
  };

  const renderList = (authors: AuthorType[]) => {
    return (
      <div className="flex justify-between">
        <div className="mt-1 flex flex-wrap">
          {authors.map((author, key) => {
            return (
              <span className="overflow flex break-words break-all" key={author.slug}>
                <Link
                  href={`/author/${author.slug}`}
                  className="font-condensed text-base font-light no-underline decoration-transparent decoration-1 underline-offset-4 transition-colors ease-in-out hover:decoration-dark-70 dark:text-white-70 dark:hover:decoration-white-70 md:underline"
                >
                  {author.name}
                </Link>
                <span className="mr-1 text-dark dark:text-white">{key + 1 !== authors.length ? "," : ""}</span>
              </span>
            );
          })}
        </div>
      </div>
    );
  };

  if (!isPending && shouldBeGone) {
    return null;
  }

  return (
    <>
      <div className="post-item flex w-full py-6">
        <div className="post-item-body w-full">
          <div>
            <Link
              href={post.local_link}
              className={`no-underline decoration-1 underline-offset-4 transition-colors ease-in-out visited:text-dark-visited hover:decoration-dark-70 dark:hover:decoration-white-70 md:underline ${
                hoveredPost ? "decoration-dark-70 dark:decoration-white-70" : "decoration-transparent"
              }`}
              onMouseEnter={() => setHoveredPost(true)}
              onMouseLeave={() => setHoveredPost(false)}
              onClick={() => handleRelatedPostLink()}
            >
              <h3 className="mb-2.5 text-xl dark:text-white sm:text-3xl">{post.title}</h3>
            </Link>
          </div>
          {renderList(post.authors)}
        </div>
        <div className="ml-4 flex flex-col items-end">
          {post?.thumbnail["full"]?.url && (
            <Link
              href={post.local_link}
              className="flex w-[116px] overflow-hidden md:w-[160px]"
              onClick={() => handleRelatedPostLink()}
            >
              <ImageWithSkeleton
                src={offlineThumbnailBlob ?? post?.thumbnail["full"]?.url!}
                alt={post.title}
                width={160}
                height={105}
                className="max-h-[105px] object-cover"
                onMouseEnter={() => setHoveredPost(true)}
                onMouseLeave={() => setHoveredPost(false)}
                isHovered={hoveredPost}
              />
            </Link>
          )}
          <div className={styles.relatedPostButtonsWrap}>
            {post.audio?.url && (
              <>
                {!isPaused && isPlaying ? (
                  <TinyPauseAudioButton
                    onClick={async () => {
                      await pause();
                    }}
                  />
                ) : (
                  <TinyPlayAudioButton
                    onClick={async () => {
                      await setTrack(post);
                      return;
                    }}
                  />
                )}
              </>
            )}
            <TinyBookmarkButton postTitle={post?.title} postId={post.id} />
            <TinyDownloadButton
              onClick={() => {
                startTransition(() => {
                  saveOrDelete(post);
                  // Optimistic update: removes the item from the list immediately.
                  setShouldBeGone(true);
                });
              }}
              downloaded={isTrackDownloaded}
              downloading={downloading}
              postTitle={post.title}
              mediaURL={post.audio?.url}
            />
          </div>
        </div>
      </div>
      <DottedSeparator />
    </>
  );
};
